import { mdiAlertCircle, mdiCheckCircle, mdiCheck } from '@mdi/js';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import * as React from 'react';
import { components, GroupBase, MenuProps, OptionProps, OptionsOrGroups } from 'react-select';
import { AsyncProps } from 'react-select/async';
import { TooltipTransitionComponent } from '../tooltip';
import { DropdownIndicator, ISelectOption } from './select';
import * as S from './select.styles';

export type IAsyncSelectProps = AsyncProps<ISelectOption, boolean, GroupBase<ISelectOption>> & {
    className?: string;
    successMessage?: string;
    errorMessage?: string;
    required?: boolean;
    disabled?: boolean;
    loadOptions: (inputValue: string) => Promise<ISelectOption[]>;
};

export const AsyncSelect: React.FunctionComponent<IAsyncSelectProps> = (
    props: IAsyncSelectProps,
): React.ReactElement => {
    const { className, successMessage, errorMessage, required, disabled, loadOptions: loadOptionsProp, ...otherProps } =
        props;

    // Keep latest loader in a ref so the debounced function identity stays stable across
    // re-renders (unstable loadOptions + cacheOptions was racing and caching empty menus).
    const loadOptionsRef = React.useRef(loadOptionsProp);
    loadOptionsRef.current = loadOptionsProp;

    const runLoadOptions = React.useCallback(
        (
            inputValue: string,
            callback: (options: OptionsOrGroups<ISelectOption, GroupBase<ISelectOption>>) => void,
        ): void => {
            loadOptionsRef
                .current(inputValue)
                .then(callback)
                .catch(() => callback([]));
        },
        [],
    );

    const debouncedLoadOptions = React.useMemo(
        () =>
            _.debounce(runLoadOptions, 1000, {
                leading: false,
                trailing: true,
            }),
        [runLoadOptions],
    );

    React.useEffect(() => {
        return () => {
            debouncedLoadOptions.cancel();
        };
    }, [debouncedLoadOptions]);

    // defaultOptions / empty query must load immediately; debounce only user typing.
    const loadOptions = React.useCallback(
        (
            inputValue: string,
            callback: (options: OptionsOrGroups<ISelectOption, GroupBase<ISelectOption>>) => void,
        ): void => {
            if (!(inputValue || '').trim()) {
                debouncedLoadOptions.cancel();
                runLoadOptions(inputValue, callback);
                return;
            }
            debouncedLoadOptions(inputValue, callback);
        },
        [debouncedLoadOptions, runLoadOptions],
    );

    const selectProps: any = {
        ...otherProps,
        styles: {
            menuPortal: base => ({
                ...base,
                zIndex: 9999,
            }),
            ...otherProps.styles,
        },
        components: {
            DropdownIndicator,
            Menu: (menuProps: MenuProps<ISelectOption>): React.ReactElement => (
                <S.MenuWrapper showSuccess={true} showError={true}>
                    <components.Menu {...(menuProps as any)} />
                </S.MenuWrapper>
            ),
            Option: (optionProps: OptionProps<ISelectOption>): React.ReactElement => {
                if (optionProps.data.title) {
                    return (
                        <components.Option {...(optionProps as any)}>
                            <Tooltip
                                placement="top-start"
                                title={optionProps.data.title}
                                TransitionComponent={TooltipTransitionComponent}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <S.SelectOptionIconWrapper>
                                        {optionProps.isSelected && <S.SelectIcon path={mdiCheck} size={0.6} />}
                                    </S.SelectOptionIconWrapper>
                                    <span>{optionProps.data.label}</span>
                                    {optionProps.data.info}
                                </div>
                            </Tooltip>
                        </components.Option>
                    );
                }

                return (
                    <components.Option {...(optionProps as any)}>
                        <S.SelectOptionIconWrapper>
                            {optionProps.isSelected && <S.SelectIcon path={mdiCheck} size={0.6} />}
                        </S.SelectOptionIconWrapper>
                        <span>{optionProps.data.label}</span>
                        {optionProps.data.info}
                    </components.Option>
                );
            },
        },
        classNamePrefix: 'select',
        openMenuOnFocus: true,
        isClearable: !required,
        isDisabled: disabled,
    };

    const handleClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
        e.preventDefault();
    };

    return (
        <S.SelectContainer className={className} onClick={handleClick}>
            <S.StyledAsyncSelect {...(selectProps as any)} loadOptions={loadOptions} />
            {successMessage && (
                <S.SelectBottomSuccess>
                    <S.SelectBottomIcon path={mdiCheckCircle} />
                    <S.SelectBottomMessage>{successMessage}</S.SelectBottomMessage>
                </S.SelectBottomSuccess>
            )}

            {errorMessage && (
                <S.SelectBottomError>
                    <S.SelectBottomIcon path={mdiAlertCircle} />
                    <S.SelectBottomMessage>{errorMessage}</S.SelectBottomMessage>
                </S.SelectBottomError>
            )}
        </S.SelectContainer>
    );
};
